import React, { useState, useEffect } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { Users, Mail, Phone, UserCircle, Shield, Search, UserPlus } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function PortalRoster() {
  const { team, userRole, canManage, token } = useOutletContext()
  const navigate = useNavigate()
  const [roster, setRoster] = useState({ players: [], staff: [], guardians: [] })
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('players')

  useEffect(() => {
    if (team?.team_id) {
      fetchRoster()
    }
  }, [team?.team_id])

  const fetchRoster = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams/${team.team_id}/roster`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (res.ok) {
        const data = await res.json()
        // Roster can be a list with type field or an object with players/staff arrays
        if (Array.isArray(data)) {
          const players = data.filter(r => r.type === 'player')
          const staff = data.filter(r => r.type === 'staff')
          const guardians = data.filter(r => r.type === 'guardian' || r.type === 'parent')
          setRoster({ players, staff, guardians })
        } else {
          setRoster(data)
        }
      }
    } catch (err) {
      console.error('Error fetching roster:', err)
    } finally {
      setLoading(false)
    }
  }

  const filterBySearch = (items, fields) => {
    if (!searchQuery) return items
    const query = searchQuery.toLowerCase()
    return items.filter(item => 
      fields.some(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item)
        return value?.toString().toLowerCase().includes(query)
      })
    )
  }

  const filteredPlayers = filterBySearch(roster.players || [], ['first_name', 'last_name', 'jersey_number', 'position'])
  const filteredStaff = filterBySearch(roster.staff || [], ['name', 'email', 'role'])
  const filteredGuardians = filterBySearch(roster.guardians || [], ['name', 'email', 'phone'])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Team Roster</h1>
          <p className="text-zinc-400 mt-1">
            {roster.players?.length || 0} players &middot; {roster.staff?.length || 0} staff
          </p>
        </div>
        
        {canManage && (
          <button
            onClick={() => navigate(`/portal/team/${team.team_id}/players/new`)}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add Player
          </button>
        )}
        
        {/* Search */}
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search roster..."
            className="w-full sm:w-64 pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-black focus:border-black outline-none"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-zinc-800 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('players')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'players' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-200'
          }`}
        >
          Players ({roster.players?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('staff')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'staff' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-200'
          }`}
        >
          Staff ({roster.staff?.length || 0})
        </button>
        {roster.guardians?.length > 0 && (
          <button
            onClick={() => setActiveTab('guardians')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'guardians' ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            Parents ({roster.guardians?.length || 0})
          </button>
        )}
      </div>

      {/* Players Tab */}
      {activeTab === 'players' && (
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
          {filteredPlayers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {searchQuery ? 'No players found' : 'No Players'}
              </h3>
              <p className="text-zinc-400">
                {searchQuery ? 'Try a different search term.' : 'Players will appear here when added to the team.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-900 border-b border-zinc-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">#</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Player</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Position</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Age</th>
                    {canManage && (
                      <th className="text-left px-6 py-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Contact</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPlayers.map((player, idx) => (
                    <tr 
                      key={player.player_id || idx} 
                      className="hover:bg-zinc-900 transition-colors cursor-pointer"
                      onClick={() => player.player_id && navigate(`/portal/team/${team.team_id}/players/${player.player_id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
                          {player.jersey_number || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
                            {player.picture || player.photo ? (
                              <img src={player.picture || player.photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <UserCircle className="w-6 h-6 text-zinc-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {player.first_name} {player.last_name}
                            </p>
                            {player.nickname && (
                              <p className="text-sm text-zinc-400">"{player.nickname}"</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-zinc-300">
                        {player.position || '-'}
                      </td>
                      <td className="px-6 py-4 text-zinc-300">
                        {player.date_of_birth ? 
                          Math.floor((new Date() - new Date(player.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)) 
                          : '-'}
                      </td>
                      {canManage && (
                        <td className="px-6 py-4">
                          {player.guardian_email || player.parent_email ? (
                            <a 
                              href={`mailto:${player.guardian_email || player.parent_email}`}
                              className="text-sm text-blue-400 hover:underline"
                            >
                              {player.guardian_email || player.parent_email}
                            </a>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Staff Tab */}
      {activeTab === 'staff' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStaff.length === 0 ? (
            <div className="col-span-full bg-zinc-800 rounded-xl border border-zinc-700 p-12 text-center">
              <Shield className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {searchQuery ? 'No staff found' : 'No Staff'}
              </h3>
              <p className="text-zinc-400">
                {searchQuery ? 'Try a different search term.' : 'Staff members will appear here.'}
              </p>
            </div>
          ) : (
            filteredStaff.map((member, idx) => (
              <div key={member.user_id || idx} className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {member.picture ? (
                      <img src={member.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-semibold text-zinc-300">
                        {member.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-semibold text-white truncate">{member.name}</h4>
                    <p className="text-sm text-zinc-400 capitalize">
                      {member.display_title || member.role?.replace('_', ' ') || 'Staff'}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-zinc-700 space-y-2">
                  {member.email && (
                    <a 
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                    >
                      <Mail className="w-4 h-4 text-zinc-500" />
                      <span className="truncate">{member.email}</span>
                    </a>
                  )}
                  {member.phone && (
                    <a 
                      href={`tel:${member.phone}`}
                      className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                    >
                      <Phone className="w-4 h-4 text-zinc-500" />
                      {member.phone}
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Guardians/Parents Tab */}
      {activeTab === 'guardians' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGuardians.length === 0 ? (
            <div className="col-span-full bg-zinc-800 rounded-xl border border-zinc-700 p-12 text-center">
              <Users className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {searchQuery ? 'No parents found' : 'No Parents'}
              </h3>
              <p className="text-zinc-400">
                {searchQuery ? 'Try a different search term.' : 'Parent contacts will appear here.'}
              </p>
            </div>
          ) : (
            filteredGuardians.map((guardian, idx) => (
              <div key={guardian.guardian_id || idx} className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <span className="text-lg font-semibold text-zinc-300">
                      {guardian.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-semibold text-white truncate">{guardian.name}</h4>
                    <p className="text-sm text-zinc-400">Parent/Guardian</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-zinc-700 space-y-2">
                  {guardian.email && (
                    <a 
                      href={`mailto:${guardian.email}`}
                      className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                    >
                      <Mail className="w-4 h-4 text-zinc-500" />
                      <span className="truncate">{guardian.email}</span>
                    </a>
                  )}
                  {guardian.phone && (
                    <a 
                      href={`tel:${guardian.phone}`}
                      className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
                    >
                      <Phone className="w-4 h-4 text-zinc-500" />
                      {guardian.phone}
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
